import { z } from "zod";
import { normalisePhone } from "./identifier.ts";

/**
 * The Lead Finder of Control Center (strategy repo: teamna/strategy/lead-finder-plan.html): the
 * swim academies and pilates studios Teamna may sell to. Pure rules and words, shared by the
 * Control Center API, its connector (MCP) and its web app.
 *
 * The score of a lead is the sum of the scores of its labels, and nothing else. A label with no
 * score counts 0. The score is never stored: change a label's score and every lead moves at once.
 */

export const leadSports = ["swimming", "pilates"] as const;
export type LeadSport = (typeof leadSports)[number];
export const leadSportSchema = z.enum(leadSports);

/** The sport a label is for. Claude applies a label only to a lead of that sport. */
export const leadLabelSports = ["swimming", "pilates", "both"] as const;
export type LeadLabelSport = (typeof leadLabelSports)[number];
export const leadLabelSportSchema = z.enum(leadLabelSports);

export const UAE_EMIRATES = ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"] as const;
export type UaeEmirate = (typeof UAE_EMIRATES)[number];
export const uaeEmirateSchema = z.enum(UAE_EMIRATES);

export const leadStatuses = ["new", "contacted", "replied", "demo", "customer", "lost", "do_not_contact"] as const;
export type LeadStatus = (typeof leadStatuses)[number];
export const leadStatusSchema = z.enum(leadStatuses);

export const LEAD_STATUS_WORDS: Record<LeadStatus, string> = {
  new: "New",
  contacted: "Contacted",
  replied: "Replied",
  demo: "Demo",
  customer: "Customer",
  lost: "Lost",
  do_not_contact: "Do not contact",
};

/** How a lead came in: a run from the laptop, a chat (the phone), or typed on the Leads page. */
export const leadSources = ["run", "chat", "by_hand"] as const;
export type LeadSource = (typeof leadSources)[number];
export const leadSourceSchema = z.enum(leadSources);

/** Where a run reads. */
export const leadRunSources = ["instagram", "facebook", "google_maps"] as const;
export type LeadRunSource = (typeof leadRunSources)[number];
export const leadRunSourceSchema = z.enum(leadRunSources);

export const LEAD_RUN_SOURCE_WORDS: Record<LeadRunSource, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  google_maps: "Google Maps",
};

export const LEAD_LIMITS = {
  /** A run never saves more than this many leads (the plan: small runs of 10 to 40). */
  runMaxLeads: 40,
  /** A run stops after this many profiles read, saved or not. */
  runMaxReads: 80,
  runSearches: 4,
  labelName: 60,
  howToSpot: 400,
  name: 120,
  note: 2000,
  reason: 300,
  url: 500,
  phonesPerLead: 4,
  labelsPerSave: 30,
  scoreMin: -1000,
  scoreMax: 1000,
} as const;

/** The text the Delete all dialog asks for, typed exactly. */
export const DELETE_ALL_LEADS_CONFIRM = "DELETE LEADS";

/** The one label Control Center applies itself, when a lead matches a Teamna business or a waitlist sign-up. */
export const ALREADY_ON_TEAMNA_KEY = "already_on_teamna";

/** The score rule: the sum of the label scores, a label with no score counts 0. */
export function leadScore(labels: ReadonlyArray<{ score: number | null }>): number {
  let total = 0;
  for (const label of labels) total += label.score ?? 0;
  return total;
}

/** True when a label of `labelSport` may go on a lead of `sport`. */
export function labelFitsSport(labelSport: LeadLabelSport, sport: LeadSport): boolean {
  return labelSport === "both" || labelSport === sport;
}

/**
 * The Instagram handle in a link or a typed handle, lower case, no "@". Null when it is not one.
 * `instagram.com/aquakids/`, `@AquaKids` and `aquakids` all give `aquakids`.
 */
export function instagramHandle(raw: string): string | null {
  let value = raw.trim();
  const fromUrl = value.match(/^(?:https?:\/\/)?(?:www\.|m\.)?instagram\.com\/([^/?#]+)/i);
  if (fromUrl) value = fromUrl[1]!;
  value = value.replace(/^@/, "").toLowerCase();
  if (!/^[a-z0-9._]{1,30}$/.test(value)) return null;
  if (["p", "reel", "reels", "explore", "stories", "accounts"].includes(value)) return null;
  return value;
}

/** The host of a website, lower case, with no "www.". Null when it is not a web address. */
export function websiteDomain(raw: string): string | null {
  const value = raw.trim();
  if (!value) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    if (!host.includes(".") || host.endsWith(".")) return null;
    return host;
  } catch {
    return null;
  }
}

/**
 * A Facebook page address with no query, lower case, so two spellings of one page dedupe.
 * Null when it is not a Facebook address.
 */
export function facebookPage(raw: string): string | null {
  const match = raw.trim().match(/^(?:https?:\/\/)?(?:www\.|m\.|web\.)?(?:facebook|fb)\.com\/([^?#]+)/i);
  if (!match) return null;
  const path = match[1]!.replace(/\/+$/, "").toLowerCase();
  if (!path || path === "profile.php") return null;
  return `facebook.com/${path}`;
}

/**
 * A business number in E.164, or null. A mobile reads as for sign-in (`normalisePhone`). A UAE
 * landline also reads, because many academies and studios show one: 04 333 4444 (8 digits after
 * the 0) and 9714 333 4444 give +97143334444.
 */
export function leadPhone(raw: string): string | null {
  const mobile = normalisePhone(raw);
  if (mobile) return mobile;
  const digits = raw.replace(/\D/g, "");
  if (/^0[1-9]\d{7}$/.test(digits)) return `+971${digits.slice(1)}`;
  if (/^971[1-9]\d{7}$/.test(digits)) return `+${digits}`;
  return null;
}

/**
 * True for an http or https address. A link a lead keeps (the Google Maps link, where a number or a
 * label was seen) becomes an `href` on the Leads page, so a `javascript:` or `data:` address must
 * never get in.
 */
export function isWebLink(raw: string): boolean {
  try {
    const url = new URL(raw.trim());
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

/** The fields a lead needs before it is saved. The connector tells Claude to find or ask for them. */
export const LEAD_REQUIRED_FIELDS = ["name", "sport", "emirate", "a phone number or an Instagram handle"] as const;
